import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { SubcategoryListParams, SubcategoryStatusFilter } from '@/types/subcategory';

export interface SubcategoryQueryState {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search: string;
  status: SubcategoryStatusFilter;
  dateFrom: string;
  dateTo: string;
  categoryIds: string[];
}

type ParamPatch = Partial<{
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search: string | null;
  status: SubcategoryStatusFilter | null;
  dateFrom: string | null;
  dateTo: string | null;
  categoryIds: string[] | null;
}>;

const DEFAULTS: SubcategoryQueryState = {
  page: 1,
  limit: 10,
  sortBy: 'sortOrder',
  sortOrder: 'asc',
  search: '',
  status: 'all',
  dateFrom: '',
  dateTo: '',
  categoryIds: [],
};

function parseStatus(value: string | null): SubcategoryStatusFilter {
  if (value === 'active' || value === 'inactive') return value;
  return 'all';
}

function parseCategoryIds(searchParams: URLSearchParams): string[] {
  const values = searchParams.getAll('categoryIds');

  if (values.length > 1) {
    return values.filter(Boolean);
  }

  const single = values[0] ?? searchParams.get('categoryId') ?? '';
  if (!single) return [];

  return single
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

export function useSubcategoryQueryParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo<SubcategoryQueryState>(() => {
    const page = Number(searchParams.get('page') || DEFAULTS.page);
    const limit = Number(searchParams.get('limit') || DEFAULTS.limit);

    return {
      page: Number.isFinite(page) && page > 0 ? page : DEFAULTS.page,
      limit: Number.isFinite(limit) && limit > 0 ? Math.min(limit, 100) : DEFAULTS.limit,
      sortBy: searchParams.get('sortBy') || DEFAULTS.sortBy,
      sortOrder: searchParams.get('sortOrder') === 'desc' ? 'desc' : 'asc',
      search: searchParams.get('search') || '',
      status: parseStatus(searchParams.get('status')),
      dateFrom: searchParams.get('dateFrom') || '',
      dateTo: searchParams.get('dateTo') || '',
      categoryIds: parseCategoryIds(searchParams),
    };
  }, [searchParams]);

  const apiParams = useMemo<SubcategoryListParams>(
    () => ({
      page: state.page,
      limit: state.limit,
      sortBy: state.sortBy,
      sortOrder: state.sortOrder,
      ...(state.search ? { search: state.search } : {}),
      ...(state.status !== 'all' ? { status: state.status } : {}),
      ...(state.dateFrom ? { dateFrom: state.dateFrom } : {}),
      ...(state.dateTo ? { dateTo: state.dateTo } : {}),
      ...(state.categoryIds.length ? { categoryIds: state.categoryIds } : {}),
    }),
    [state],
  );

  const patchParams = useCallback(
    (patch: ParamPatch, options?: { resetPage?: boolean }) => {
      setSearchParams(
        (searchParams) => {
          const next = new URLSearchParams(searchParams);
          const shouldResetPage = options?.resetPage ?? false;

          if (shouldResetPage) {
            next.set('page', '1');
          }

          const apply = (
            key: string,
            value: string | null | undefined,
            omitValues: string[] = [],
          ) => {
            if (value === null || value === undefined || value === '' || omitValues.includes(value)) {
              next.delete(key);
              return;
            }
            next.set(key, value);
          };

          if (patch.page !== undefined) next.set('page', String(patch.page));
          if (patch.limit !== undefined) next.set('limit', String(patch.limit));
          if (patch.sortBy !== undefined) next.set('sortBy', patch.sortBy);
          if (patch.sortOrder !== undefined) next.set('sortOrder', patch.sortOrder);
          if (patch.search !== undefined) apply('search', patch.search);
          if (patch.status !== undefined) apply('status', patch.status, ['all']);
          if (patch.dateFrom !== undefined) apply('dateFrom', patch.dateFrom);
          if (patch.dateTo !== undefined) apply('dateTo', patch.dateTo);
          if (patch.categoryIds !== undefined) {
            next.delete('categoryId');
            next.delete('categoryIds');
            if (patch.categoryIds?.length) {
              next.set('categoryIds', patch.categoryIds.join(','));
            }
          }

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const resetAll = useCallback(() => {
    setSearchParams(
      (searchParams) => {
        const next = new URLSearchParams(searchParams);
        next.delete('search');
        next.delete('status');
        next.delete('dateFrom');
        next.delete('dateTo');
        next.delete('categoryId');
        next.delete('categoryIds');
        next.delete('sortBy');
        next.delete('sortOrder');
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const hasActiveFiltersOrSort = useMemo(
    () =>
      state.search !== '' ||
      state.status !== 'all' ||
      state.dateFrom !== '' ||
      state.dateTo !== '' ||
      state.categoryIds.length > 0 ||
      state.sortBy !== DEFAULTS.sortBy ||
      state.sortOrder !== DEFAULTS.sortOrder,
    [state],
  );

  return { state, apiParams, patchParams, resetAll, hasActiveFiltersOrSort };
}
